import React, { ChangeEvent } from 'react';

// Style
import "components/Carts/CartProject/CartProject.scss";

// Lib
import { sanitize } from 'dompurify';
import { useWindowSize } from 'lib/hooks';
import { useAppDispatch } from 'lib/hooks/useDispatch';
import { convertDataPrices, filteredDataPrices, optionsPrices } from 'lib/helpers';

// Classnames
import clsx from 'clsx';

// Components
import Img from 'components/Img';
import Button from 'components/UiKit/Button';
import DropdownPrice from 'components/DropdownPrice';
import CountControll from 'components/CountControll';
import FieldCheckbox from 'components/UiKit/FieldCheckbox';
import ListSupplierLogo from 'components/ListSupplierLogo';
import InStock from 'pages/MoleculeProduct/components/InStock';
import OutOfStock from 'pages/MoleculeProduct/components/OutOfStock';

// Actions
import * as actionCreatorsLibCompounds from 'pages/LibrariesCompounds/reducer';

// Config
import config from 'lib/config';

// Type
import { Supplier } from 'pages/MoleculeProduct/reducer';

type TMap = {
	[index: string]: string | number | boolean
};
type TPrice = {
	mg?: number,
	priceEur?: number,
	priceUsd?: number,
	available?: boolean,
};

// Interface
interface ICartProject {
	id?: string,
	cas?: string,
	name?: string,
	dbId?: string,
	isPOA: boolean;
	currentTypePrice: string;
	prices: {
		supplier?: string | undefined;
		prices?: TPrice[] | undefined;
	}[],
	onStock?: boolean,
	cxSmiles?: string,
	quantity?: number,
	iupacName?: string,
	selected?: boolean,
	description?: string,
	supIds?: Supplier[],
	currentPrice?: any,
	numeration?: string,
}

const CartLibrariesCompounds: React.FC<ICartProject> = (props) => {
	const {
		id,
		cas,
		name,
		supIds,
		prices,
		onStock,
		isPOA,
		quantity,
		selected,
		numeration,
		currentPrice,
		currentTypePrice
	} = props;
	const dispatch = useAppDispatch();

	let titlePOA: string = '';
	let countPrice: number = 0;
	const { width } = useWindowSize();
	const isDesktop = width < 1024;
	const currentPrices: any = prices?.length
		? prices.find(
			(item: any) => item.supplier === currentTypePrice
		)
		: { prices: [], supplier: 'EBC' };
	const convertedOptions = convertDataPrices(currentPrices?.prices);
	const optionsRadioTypePrices = prices?.length ? prices.map((item: any) => ({
		label: item.supplier,
		value: item.supplier
	})) : [];
	const src = `${config.api.apiUrl}ebc/catalog/${id}/image?format=png:-a,w${110 * 4},h${110 * 4},noantialias,H_hetero`;

	const handlerLink = (id: string) => () => {
		const link = document.createElement('a');

		link.href = `${window.location.origin}/molecule-product/${id}`;
		link.target = '_blank';
		link.click();
		link.remove();
	};

	const handlerChangeTypePrice = (type: string) => {
		dispatch(actionCreatorsLibCompounds.handlerChangeCurrentTypePrice({
			id: id,
			type: type
		}));
		dispatch(actionCreatorsLibCompounds.handlerChangePrice({
			id: id,
			currentPrice: {}
		}));
	};

	const handlerClickPlus = (number: number) => {
		dispatch(actionCreatorsLibCompounds.handlerChangeQuantity({
			id: id,
			type: 'plus',
			quantity: number
		}));
	};

	const handlerClickMinus = (number: number) => {
		if ((quantity || 0) - 1) {
			dispatch(actionCreatorsLibCompounds.handlerChangeQuantity({
				id: id,
				type: 'minus',
				quantity: number
			}));
		}
	};

	const handlerChangeCheckbox = (id: string) => (e: ChangeEvent<HTMLInputElement>) => {
		dispatch(actionCreatorsLibCompounds.handlerChangeCheckboxSelect({
			id: id,
			event: e
		}));
	};

	const handlerChangePrices = (data: TMap) => {
		dispatch(actionCreatorsLibCompounds.handlerChangePrice({
			id: id,
			currentPrice: data
		}));
	};

	const getTitle = (data: Supplier[]) => {
		return <div className="cart-project__wrapper-product-id flex mb5">
			<ListSupplierLogo
				data={data}
				className="mb10"
				isHiddeBorder={isDesktop}
				classNameWrapperLogo="mr5"
				classNameText="cart-project__product-id"
			/>
		</div>;
	};

	const options = currentPrices?.prices?.length
		? filteredDataPrices(currentPrices.prices)?.map((item: TMap) =>
			optionsPrices(item)
		) : [];

	const cnWrapper = clsx(
		'cart-project relative flex flex-ai-start flex-direction-column-mobile',
		!selected ? 'cart-project--disabled' : !onStock ? 'cart-project--disabled' : ''
	);

	options.forEach((item: TMap) => {
		if (item.value === undefined) {
			countPrice += 1;
		}
		return item;
	});

	if (options?.length === countPrice) {
		titlePOA = 'Price on asking';
	}

	return (
		<div className={cnWrapper}>
			<div className="cart-project__left-content flex flex-ai-start flex-direction-column-desktop">
				<FieldCheckbox
					label={numeration}
					name="checkboxItemProject"
					value={onStock && selected}
					onChange={handlerChangeCheckbox(id || '')}
					disabled={!onStock || isPOA}
				/>
				<div className="cart-project__left-content-wrapper flex flex-ai-start flex-jc-between">
					<Img
						alt={id}
						isPreloader
						innerSrc={src}
						classNameWrapper="flex flex-ai-center flex-jc-center"
						className="cart-project__img mr10"
						classNameImgSvg="cart-project__img display-none-mobile mr10"
					/>

					<div className="flex flex-direction-column">
						{getTitle(supIds || [])}
						<p className="cart-project__cas mb5">
							{cas}
						</p>
						<p
							className="cart-project__name"
							dangerouslySetInnerHTML={{ __html: sanitize(name || '') }}
						/>
					</div>
				</div>
			</div>

			<div className="cart-project__right-content flex flex-ai-start flex-direction-column-mobile">
				<div className="cart-project__right-content-wrapper flex flex-ai-center flex-ai-start-mobile flex-direction-column-mobile full-width flex-jc-end">

					<div
						className="cart-project__text-width flex flex-ai-center flex-ai-start-mobile flex-direction-column-mobile">
						{onStock && <InStock />}
						{!onStock && <OutOfStock />}
					</div>

					<DropdownPrice
						titlePOA={titlePOA}
						withUrlParam={false}
						className="ml10 mr10"
						options={convertedOptions}
						currentPrice={currentPrice}
						typePrice={currentTypePrice}
						onChange={handlerChangePrices}
						optionsRadio={optionsRadioTypePrices}
						handlerChangeTypePrice={handlerChangeTypePrice}
					/>

					<CountControll
						isCart
						isHideTitle
						innerCount={quantity}
						innderDisabled={!selected}
						handlerPlus={handlerClickPlus}
						handlerMinus={handlerClickMinus}
						className="count__cart flex-jc-end"
					/>

					<div className="cart-project__wrapper-btn flex flex-jc-end flex-ai-center">
						<Button
							isSvg
							icon="linkSvg"
							variable="icon"
							title="Go to the product details"
							onPress={handlerLink(id || '')}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

export default CartLibrariesCompounds;